import React from 'react';
import { COLORS } from '../../../core/constants/colors';
import { FileUpload } from '../../dashboard_user/components/FileUpload';
import { Company, Document } from '../../dashboard_user/types';
import { EnrichmentSession } from '../types';
import { ExistingSessionConflictModal } from './ExistingSessionConflictModal';

interface UploadStepProps {
  company: Company | null;
  existingDocuments: Document[];
  selectedExistingDocumentIds: number[];
  loadingDocuments: boolean;
  onToggleExistingDocument: (documentId: number) => void;
  onUploadComplete: (documentId: number) => void;
  onStartEnrichment: () => void;
  onBack: () => void;
  readyToStart: boolean;
  startingSession: boolean;
  sessionError: string | null;
  existingSessionConflict: {
    sessionId: string;
    session: EnrichmentSession | null;
    loading: boolean;
  } | null;
  onViewExistingSession: () => void;
  onSkipExistingSession: () => void;
  onCancelExistingSession: () => void;
  onCloseConflictModal: () => void;
}

export const UploadStep: React.FC<UploadStepProps> = ({
  company,
  existingDocuments,
  selectedExistingDocumentIds,
  loadingDocuments,
  onToggleExistingDocument,
  onUploadComplete,
  onStartEnrichment,
  onBack,
  readyToStart,
  startingSession,
  sessionError,
  existingSessionConflict,
  onViewExistingSession,
  onSkipExistingSession,
  onCancelExistingSession,
  onCloseConflictModal,
}) => {
  return (
    <div className="flex-1">
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:bg-gray-100"
          style={{ color: COLORS.primary.persianIndigo }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>

      {/* Header with Icon */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: COLORS.extended.azure + '15' }}>
            <svg className="w-6 h-6" style={{ color: COLORS.extended.azure }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-bold" style={{ color: COLORS.primary.persianIndigo }}>
              Upload Documents
            </h2>
          </div>
        </div>
        <p className="text-sm" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
          Upload your portfolio and supporting documents
        </p>
      </div>

      <div className="space-y-5">
        {company?.id ? (
          <>
            {/* Upload Section */}
            <div className="bg-white rounded-2xl p-6">
              <FileUpload 
                companyId={company.id} 
                onUploadComplete={onUploadComplete}
                existingDocuments={existingDocuments}
                selectedExistingDocumentIds={selectedExistingDocumentIds}
                onToggleExistingDocument={onToggleExistingDocument}
                loadingExistingDocuments={loadingDocuments}
              />
            </div>

            {/* Error/Conflict Messages */}
            {sessionError && !existingSessionConflict && (
              <div className="p-4 rounded-lg" style={{ backgroundColor: COLORS.extended.redSalsa + '20' }}>
                <p className="text-sm" style={{ color: COLORS.extended.redSalsa }}>
                  {sessionError}
                </p>
              </div>
            )}

            {/* Start Enrichment Button */}
            <div className="pt-4">
              <button
                onClick={onStartEnrichment}
                disabled={!readyToStart || startingSession || !!existingSessionConflict}
                className="w-full py-3 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: readyToStart && !existingSessionConflict ? COLORS.extended.azure : '#D1D5DB' }}
              >
                {startingSession ? 'Starting...' : 'Start Enrichment'}
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-sm" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
              Please complete company information first
            </p>
          </div>
        )}
      </div>

      {/* Existing Session Conflict Modal */}
      {existingSessionConflict && (
        <ExistingSessionConflictModal
          conflict={existingSessionConflict}
          onView={onViewExistingSession}
          onSkip={onSkipExistingSession}
          onCancel={onCancelExistingSession}
          onClose={onCloseConflictModal}
          isProcessing={startingSession}
        />
      )}
    </div>
  );
};

